import { Component, Input, OnChanges, SimpleChanges, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { firstValueFrom } from 'rxjs';
import { CameraEventRecordDto, CameraEventCode, CameraEventRecordDtoPaginationResult, PointDirection } from '../../../../../../../api/gen';
import { CameraEventService } from '../../../../../../../api/gen/api/cameraEvent.service';

@Component({
  selector: 'app-camera-event-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatIconModule,
    MatChipsModule,
    MatTooltipModule
  ],
  templateUrl: './camera-event-list.component.html',
  styleUrl: './camera-event-list.component.scss'
})
export class CameraEventListComponent implements OnChanges {
  @Input() fetchId?: string;
  @Input() cameraId?: string;

  records = signal<CameraEventRecordDto[]>([]);
  totalCount = signal(0);
  isLoading = signal(false);
  pageIndex = signal(0);
  pageSize = signal(25);

  readonly displayedColumns = ['plateNumber', 'eventType', 'direction', 'eventTime', 'country', 'plateColor', 'vehicleColor', 'category', 'location', 'details'];
  readonly EventCode = CameraEventCode;
  readonly Direction = PointDirection;
  expandedRow: CameraEventRecordDto | null = null;

  private cameraEventService = inject(CameraEventService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fetchId'] && this.fetchId) {
      this.pageIndex.set(0);
      this.loadRecords();
    }
  }

  async loadRecords(): Promise<void> {
    if (!this.fetchId) return;
    this.isLoading.set(true);
    try {
      const result = await firstValueFrom<CameraEventRecordDtoPaginationResult>(
        this.cameraEventService.cameraEventsRecordsGet(
          this.cameraId,
          this.fetchId,
          undefined,
          undefined,
          undefined,
          undefined,
          this.pageIndex(),
          this.pageSize()
        )
      );
      this.records.set(result.items ?? []);
      this.totalCount.set(result.totalCount ?? 0);
    } catch {
      this.records.set([]);
      this.totalCount.set(0);
    } finally {
      this.isLoading.set(false);
    }
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.loadRecords();
  }

  getEventTypeColor(eventType?: CameraEventCode): string {
    switch (eventType) {
      case CameraEventCode.In: return 'primary';
      case CameraEventCode.Out: return 'warn';
      case CameraEventCode.Junction: return 'accent';
      default: return '';
    }
  }

  getEventTypeIcon(eventType?: CameraEventCode): string {
    switch (eventType) {
      case CameraEventCode.In: return 'login';
      case CameraEventCode.Out: return 'logout';
      case CameraEventCode.Junction: return 'swap_horiz';
      default: return 'circle';
    }
  }

  getDirectionIcon(direction?: PointDirection): string {
    switch (direction) {
      case PointDirection.Approach: return 'south';
      case PointDirection.Departure: return 'north';
      case PointDirection.Both: return 'swap_vert';
      default: return 'help_outline';
    }
  }

  toggleExpand(row: CameraEventRecordDto): void {
    this.expandedRow = this.expandedRow === row ? null : row;
  }
}
